import { Box, Typography } from '@mui/material';
import React, { useCallback } from 'react';
import { FighterInfo } from '../../../../../../corelogic/domain/models/fighterInfo.model';
import { SeasonFights } from '../../../../../../corelogic/domain/models/seasonFights.model';

interface SeasonInfoViewerProps {
  seasonFights: SeasonFights[];
  fighterInfoArray: FighterInfo[];
}

const SeasonInfoViewer: React.FC<SeasonInfoViewerProps> = ({
  seasonFights,
  fighterInfoArray,
}) => {
  // Get the next event date and all fights from that event
  const getNextEvent = useCallback(() => {
    const today = new Date();
    const upcomingFights = seasonFights
      .filter((fight) => new Date(fight.date) > today)
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    if (upcomingFights.length === 0) return null;

    const nextEventDate = new Date(upcomingFights[0].date);
    const nextEventName = upcomingFights[0].event_name;

    // Get all fights from the same event
    const nextEventFights = upcomingFights.filter(
      (fight) =>
        new Date(fight.date).getTime() === nextEventDate.getTime() &&
        fight.event_name === nextEventName,
    );

    return {
      date: nextEventDate,
      name: nextEventName,
      fights: nextEventFights,
    };
  }, [seasonFights]);

  // Helper function to get unique upcoming and completed events
  const getEvents = useCallback(() => {
    const today = new Date();
    const completedEvents = new Map<string, Date>();
    const upcomingEvents = new Map<string, Date>();
    const nextEventName = getNextEvent()?.name;

    seasonFights.forEach((fight) => {
      const fightDate = new Date(fight.date);
      if (fightDate < today) {
        completedEvents.set(fight.event_name, fightDate);
      } else if (fight.event_name !== nextEventName) {
        // Only add to upcoming if it's not the next event
        upcomingEvents.set(fight.event_name, fightDate);
      }
    });

    return {
      completed: Array.from(completedEvents.entries()),
      upcoming: Array.from(upcomingEvents.entries()),
    };
  }, [seasonFights, getNextEvent]);

  // Get fighters from your team competing in any fight in the next event
  const getFightersInNextEvent = useCallback(
    (nextEvent: { fights: SeasonFights[] } | null) => {
      if (!nextEvent) return [];

      return fighterInfoArray.filter((fighter) =>
        nextEvent.fights.some(
          (fight) =>
            fighter.FighterName === fight.fighter_1 ||
            fighter.FighterName === fight.fighter_2,
        ),
      );
    },
    [fighterInfoArray],
  );

  const { completed, upcoming } = getEvents();
  const nextEvent = getNextEvent();
  const fightersInNextEvent = getFightersInNextEvent(nextEvent);

  const SectionTitle = ({ title }: { title: string }) => (
    <Typography
      sx={{
        fontFamily: 'Rubik Mono One',
        color: '#cc0000',
        fontSize: {
          xs: '0.9rem',
          sm: 'clamp(0.9rem, 1.0vw, 1.2rem)',
        },
        marginBottom: '10px',
        borderBottom: '1px solid rgba(255, 255, 255, 0.3)',
        paddingBottom: '5px',
      }}
    >
      {title}
    </Typography>
  );

  const EventList = ({
    events,
    title,
  }: {
    events: [string, Date][];
    title: string;
  }) => (
    <Box sx={{ marginBottom: '20px' }}>
      <SectionTitle title={title} />
      {events.map(([eventName, date], index) => (
        <Box
          key={index}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '5px',
            paddingRight: '10px',
            gap: '10px',
          }}
        >
          <Typography
            sx={{
              color: 'white',
              fontSize: {
                xs: '0.8rem',
                sm: 'clamp(0.8rem, 0.7vw, 1.0rem)',
              },
              flex: '1',
              wordWrap: 'break-word',
            }}
          >
            {eventName}
          </Typography>
          <Typography
            sx={{
              color: '#cccccc',
              fontSize: {
                xs: '0.8rem',
                sm: 'clamp(0.8rem, 0.7vw, 1.0rem)',
              },
              flexShrink: 0,
              paddingTop: '2px',
            }}
          >
            {date.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
            })}
          </Typography>
        </Box>
      ))}
    </Box>
  );

  return (
    <Box
      sx={{
        width: '100%',
        height: 'calc(100vh - 100px)',
        bgcolor: 'rgba(20, 20, 20, 0.9)',
        border: '2px solid white',
        borderRadius: '8px',
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
      }}
    >
      {/* Fixed header section - doesn't scroll */}
      <Typography
        sx={{
          fontFamily: 'Rubik Mono One',
          color: 'white',
          fontSize: {
            xs: '0.9rem',
            sm: 'clamp(0.9rem, 1.2vw, 1.2rem)',
          },
          padding: '10px 20px',
          textAlign: 'center',
          flexShrink: 0,
          bgcolor: '#8B0000',
        }}
      >
        Season Information
      </Typography>

      {/* Scrollable content section - takes remaining height */}
      <Box
        sx={{
          padding: '20px',
          overflowY: 'auto',
          flexGrow: 1,
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(255, 255, 255, 0.3)',
            borderRadius: '4px',
            '&:hover': {
              background: 'rgba(255, 255, 255, 0.4)',
            },
          },
        }}
      >
        {nextEvent && (
          <Box sx={{ marginBottom: '20px' }}>
            <SectionTitle title="Next Event" />
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '10px',
                paddingRight: '10px',
                gap: '10px',
              }}
            >
              <Typography
                sx={{
                  color: 'white',
                  fontSize: {
                    xs: '0.8rem',
                    sm: 'clamp(0.8rem, 0.7vw, 1.0rem)',
                  },
                  flex: '1',
                  wordWrap: 'break-word',
                }}
              >
                {nextEvent.name}
              </Typography>
              <Typography
                sx={{
                  color: '#cccccc',
                  fontSize: {
                    xs: '0.8rem',
                    sm: 'clamp(0.8rem, 0.7vw, 1.0rem)',
                  },
                  flexShrink: 0,
                  paddingTop: '2px',
                }}
              >
                {nextEvent.date.toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                })}
              </Typography>
            </Box>
            {/* Fighters Competing section remains unchanged */}
            {fightersInNextEvent.length > 0 && (
              <Box sx={{ marginBottom: '10px' }}>
                <Typography
                  sx={{
                    color: '#cc0000',
                    fontSize: {
                      xs: '0.8rem',
                      sm: 'clamp(0.8rem, 1vw, 1.0rem)',
                    },
                    fontFamily: 'Rubik Mono One',
                    marginBottom: '5px',
                  }}
                >
                  Fighters Competing
                </Typography>
                {fightersInNextEvent.map((fighter, index) => (
                  <Typography
                    key={index}
                    sx={{
                      color: 'white',
                      fontSize: {
                        xs: '0.8rem',
                        sm: 'clamp(0.8rem, 0.7vw, 1.0rem)',
                      },
                      marginBottom: '5px',
                      marginLeft: '10px',
                    }}
                  >
                    {fighter.FighterName}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
        )}

        <EventList
          events={[...upcoming].sort((a, b) => a[1].getTime() - b[1].getTime())}
          title="Upcoming Events"
        />
        <EventList
          events={[...completed].sort(
            (a, b) => a[1].getTime() - b[1].getTime(),
          )}
          title="Completed Events"
        />
      </Box>
    </Box>
  );
};

export default SeasonInfoViewer;
